/**
 * graphLayout — ELK-based layout for the vault graph view.
 *
 * Ports the layout call from
 * `with_obsidian/localvaultexplorer/packages/web/src/components/graph/graphLayout.ts`
 * but trimmed to two layout modes (layered + force) and our adjacency
 * shape (wikilinks + embeds). ELK runs in-browser via the WASM build
 * shipped by `elkjs/lib/elk.bundled.js`.
 *
 * Inputs are framework-agnostic — `nodes` and `edges` are typed in the
 * caller's terms. Output is XYFlow-ready: `{x, y}` positions on every
 * node, plus `width` / `height` defaults applied here so the React-Flow
 * tree doesn't have to re-measure.
 */

import ELK from 'elkjs/lib/elk.bundled.js';

const elk = new ELK();

export type LayoutMode = 'layered' | 'force';

export interface VaultGraphNode {
  id: string;
  /** Display label. */
  label: string;
  /** Sort weight — used to bias layout direction in force mode. */
  weight?: number;
  /** Width / height defaults; ELK ignores nodes without dimensions. */
  width?: number;
  height?: number;
}

export interface VaultGraphEdge {
  id: string;
  source: string;
  target: string;
  kind: 'wikilink' | 'embed' | 'mdlink' | 'tag';
}

export interface LaidOutNode {
  id: string;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  weight: number;
}

export interface LaidOutGraph {
  nodes: LaidOutNode[];
  edges: VaultGraphEdge[];
}

const DEFAULT_NODE_WIDTH = 140;
const DEFAULT_NODE_HEIGHT = 36;

interface ElkChild {
  id: string;
  width: number;
  height: number;
  x?: number;
  y?: number;
  labels?: { text: string }[];
  layoutOptions?: Record<string, string>;
}

interface ElkEdge {
  id: string;
  sources: string[];
  targets: string[];
}

interface ElkGraph {
  id: string;
  layoutOptions: Record<string, string>;
  children: ElkChild[];
  edges: ElkEdge[];
}

function makeOptions(mode: LayoutMode): Record<string, string> {
  if (mode === 'layered') {
    return {
      'elk.algorithm': 'layered',
      'elk.direction': 'RIGHT',
      'elk.layered.spacing.nodeNodeBetweenLayers': '60',
      'elk.spacing.nodeNode': '24',
      'elk.layered.crossingMinimization.semiInteractive': 'true',
    };
  }
  // Force mode — actually ELK's "stress" algorithm; produces an organic
  // map-style layout suitable for highly interconnected vaults.
  return {
    'elk.algorithm': 'stress',
    'elk.stress.dimension': 'XY',
    'elk.spacing.nodeNode': '60',
  };
}

/**
 * Compute positions for all nodes. Stable across runs given identical
 * input; deterministic order is enforced by sorting nodes by id before
 * handing them to ELK.
 */
export async function computeVaultGraphLayout(
  nodes: VaultGraphNode[],
  edges: VaultGraphEdge[],
  mode: LayoutMode = 'force',
): Promise<LaidOutGraph> {
  const sortedNodes = [...nodes].sort((a, b) => a.id.localeCompare(b.id));
  const sortedEdges = [...edges].sort((a, b) => a.id.localeCompare(b.id));

  const elkNodes: ElkChild[] = sortedNodes.map((n) => ({
    id: n.id,
    width: n.width ?? DEFAULT_NODE_WIDTH,
    height: n.height ?? DEFAULT_NODE_HEIGHT,
    labels: [{ text: n.label }],
  }));
  const elkEdges: ElkEdge[] = sortedEdges.map((e) => ({
    id: e.id,
    sources: [e.source],
    targets: [e.target],
  }));

  const graph: ElkGraph = {
    id: 'vault-graph-root',
    layoutOptions: makeOptions(mode),
    children: elkNodes,
    edges: elkEdges,
  };

  const result = (await elk.layout(graph as unknown as Parameters<typeof elk.layout>[0])) as unknown as ElkGraph;
  const laid: LaidOutNode[] = (result.children ?? []).map((c, i) => ({
    id: c.id,
    label: sortedNodes[i]?.label ?? c.id,
    x: c.x ?? 0,
    y: c.y ?? 0,
    width: c.width,
    height: c.height,
    weight: sortedNodes[i]?.weight ?? 0,
  }));
  return { nodes: laid, edges: sortedEdges };
}

/**
 * Build the inputs for `computeVaultGraphLayout` from a flat list of
 * parsed notes. Wikilinks and embeds become edges; mdlinks and tag
 * edges are skipped here (toggle in caller if you want them).
 */
export function buildVaultGraphInputs(
  notes: { path: string; name: string; wikilinks: string[]; embeds: string[] }[],
): { nodes: VaultGraphNode[]; edges: VaultGraphEdge[] } {
  const byPath = new Map<string, { path: string; name: string }>();
  const byName = new Map<string, { path: string; name: string }>();
  for (const n of notes) {
    byPath.set(n.path, { path: n.path, name: n.name });
    byPath.set(n.path.replace(/\.md$/i, ''), { path: n.path, name: n.name });
    byName.set(n.name, { path: n.path, name: n.name });
  }
  function resolve(target: string): string | null {
    return (
      byPath.get(target)?.path ??
      byPath.get(`${target}.md`)?.path ??
      byName.get(target)?.path ??
      null
    );
  }

  const nodes: VaultGraphNode[] = notes.map((n) => ({
    id: n.path,
    label: n.name,
    weight: n.wikilinks.length + n.embeds.length,
  }));

  const edges: VaultGraphEdge[] = [];
  let counter = 0;
  for (const n of notes) {
    for (const t of n.wikilinks) {
      const to = resolve(t);
      if (to) {
        edges.push({
          id: `e${counter++}`,
          source: n.path,
          target: to,
          kind: 'wikilink',
        });
      }
    }
    for (const t of n.embeds) {
      const to = resolve(t);
      if (to) {
        edges.push({
          id: `e${counter++}`,
          source: n.path,
          target: to,
          kind: 'embed',
        });
      }
    }
  }
  return { nodes, edges };
}
